import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { Close, Upload } from "@mui/icons-material";
import type { Job } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { useFieldNotesColumnViewModel } from "./FieldNotes.column.vm";
import { FieldNotesColumnViewModel } from "./FieldNotes.column.types";
import { useValidationErrors } from "../../ValidationErrorsContext";

interface FieldNotesColumnCellProps {
	row: MRT_Row<Job>;
	vm: FieldNotesColumnViewModel;
}

/**
 * Memoized Cell component for FieldNotes column
 * Prevents unnecessary re-renders when column definitions change
 */
const FieldNotesColumnCell = React.memo(
	function FieldNotesColumnCell({ row, vm }: FieldNotesColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const files = vm.getCurrentFiles(job);
		const validationKey = "fieldNotes";
		const errorKey = getValidationErrorKey(validationKey, job.id);
		const hasError = !!validationErrors[errorKey];

		return (
			<Stack spacing={1} sx={{ width: "100%", padding: 1 }}>
				{files.map((file, index) => (
					<Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
							{file.name}
						</Typography>
						<Button
							size="small"
							startIcon={<Close />}
							onClick={() => {
								vm.handleRemoveFile(job.id, index);
							}}
						>
							Remove
						</Button>
					</Box>
				))}
				<Box>
					<input
						type="file"
						multiple
						id={`fieldNotes-upload-${job.id}`}
						style={{ display: "none" }}
						onChange={(e) => {
							const selectedFiles = e.target.files;
							if (!selectedFiles || selectedFiles.length === 0) return;

							vm.handleAddFiles(job.id, selectedFiles);

							// Clear the input so the same file can be selected again
							e.target.value = "";
						}}
					/>
					<Button
						size="small"
						variant="outlined"
						startIcon={<Upload />}
						onClick={() => {
							const input = document.getElementById(`fieldNotes-upload-${job.id}`) as HTMLInputElement;
							input?.click();
						}}
						sx={{ width: "100%" }}
					>
						Upload Files
					</Button>
					{hasError && (
						<Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
							{validationErrors[errorKey]}
						</Typography>
					)}
				</Box>
			</Stack>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for better memoization
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		// Compare job id and field notes files
		if (prevJob.id !== nextJob.id) return false;

		const prevFiles = prevJob.files?.fieldNotes || [];
		const nextFiles = nextJob.files?.fieldNotes || [];

		if (prevFiles.length !== nextFiles.length) return false;
		// Simple check - if files array reference changed
		if (prevFiles !== nextFiles) return false;

		return true;
	},
);

/**
 * Build the FieldNotes column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function column_FieldNotes(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	const displayName = columnDisplayNames["fieldNotes"];
	const validationKey = "fieldNotes";
	const vm = useFieldNotesColumnViewModel(context);

	return {
		id: validationKey,
		header: displayName,
		accessorFn: (row) => row.files?.fieldNotes,
		enableEditing: false,
		minSize: 300,
		Cell: ({ row }: { row: MRT_Row<Job> }) => <FieldNotesColumnCell row={row} vm={vm} />,
	};
}
